import { z } from 'zod/v4-mini';

import {
  ApiDataSourceSpec,
  apiDataSource,
} from '@/io/datasource/ApiDatasource';
import {
  SessionStorageDataSourceSpec,
  sessionStorageDataSource,
} from '@/io/datasource/SessionStorageDatasource.ts';
import {
  CreateMessage,
  Message,
  messageSchema,
  unreadMessageCountSchema,
} from '@/models/MessagingModel.ts';
import {
  CursorPagination,
  cursorPaginationSchema,
} from '@/models/pagination/CursorPagination.ts';
import { cz } from '@/utils/zodUtils.ts';

export class MessagingRepository {
  constructor(
    private readonly datasource: ApiDataSourceSpec = apiDataSource,
    private readonly sessionDatasource: SessionStorageDataSourceSpec = sessionStorageDataSource,
  ) {}

  getMessages = (
    subjectId: string,
    cursor: string | undefined,
  ): Promise<CursorPagination<Message>> => {
    return this.datasource.get(
      `/practitioner/messaging/${subjectId}/`,
      cursorPaginationSchema(messageSchema),
      {
        params: {
          cursor: cursor,
        },
      },
    );
  };

  readMessages = (patientId: string) => {
    return this.datasource.post(
      `/practitioner/messaging/${patientId}/read_messages/`,
      cz.unsafe<Message[]>(),
    );
  };

  getUnreadMessages = (patientId: string) => {
    return this.datasource.get(
      `/practitioner/messaging/${patientId}/get_unread_messages/`,
      unreadMessageCountSchema,
    );
  };

  createMessage = (
    patientId: string,
    data: CreateMessage,
  ): Promise<undefined> =>
    this.datasource.post(
      `/practitioner/messaging/${patientId}/`,
      cz.ignore(),
      this._prepareMessageData(data),
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      },
    );

  _prepareMessageData = (data: CreateMessage) => {
    const { attachments, ...restData } = data;
    const formData = new FormData();

    if (attachments) {
      for (const i of attachments) {
        if (i.file) {
          formData.append('attachments', i.file);
        }
      }
    }

    for (const itemIndex in restData) {
      const itemData = restData[itemIndex as keyof typeof restData];
      if (itemData) {
        formData.append(itemIndex, itemData);
      }
    }

    return formData;
  };

  deleteMessage = (
    patientId: string,
    messageId: string,
  ): Promise<undefined> => {
    return this.datasource.delete(
      `/practitioner/messaging/${patientId}/${messageId}/`,
      cz.ignore(),
    );
  };

  draftMessage = (userId: string, patientId: string, message: string) => {
    return this.sessionDatasource.store(
      `${userId}-${patientId}`,
      'message',
      message,
    );
  };

  clearDraftMessage = (userId: string, patientId: string) => {
    return this.sessionDatasource.remove(`${userId}-${patientId}`, 'message');
  };

  getDraftMessage = (practitionerId: string, patientId: string) => {
    return this.sessionDatasource.retrieve(
      `${practitionerId}-${patientId}`,
      'message',
      z.string(),
    );
  };
}
